import { useState, useEffect, useRef } from 'react';
import { API } from '../../config/api';
import { api } from '../../context/AuthContext';
import { MotionTheme } from '../MotionTheme';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Palette, Upload, Loader2, Eye, Save, Sparkles, CloudRain, Snowflake, Zap, Star, Wind, Film, Ban, Flame, Music, Activity, Radio, Waves, CloudFog, Code, Orbit } from 'lucide-react';
import { THEMES as PLAYER_THEMES } from '../ThemedPlayer';

const EFFECT_OPTIONS = [
  { id: 'embers', label: 'Embers', icon: Flame, desc: 'Floating embers rising with warm glow' },
  { id: 'rain', label: 'Rain', icon: CloudRain, desc: 'Rainfall effect with streaking drops' },
  { id: 'snow', label: 'Snow', icon: Snowflake, desc: 'Gentle snowfall with drifting flakes' },
  { id: 'stars', label: 'Starfield', icon: Star, desc: 'Twinkling stars across the sky' },
  { id: 'electric', label: 'Electric', icon: Zap, desc: 'Lightning bolt flashes' },
  { id: 'ice_rain', label: 'Ice Crystals', icon: Sparkles, desc: 'Falling ice crystal shards' },
  { id: 'music_notes', label: 'Music Notes', icon: Music, desc: 'Floating musical note symbols' },
  { id: 'audio_waveform', label: 'Audio Waveform', icon: Waves, desc: 'Multi-layer flowing audio wave — gradient pulse' },
  { id: 'cinematic_smoke', label: 'Cinematic Smoke', icon: CloudFog, desc: 'Volumetric smoke drifting up with glowing embers' },
  { id: 'digital_rain', label: 'Digital Rain', icon: Code, desc: 'Matrix-style falling light streaks' },
  { id: 'galaxy_dust', label: 'Galaxy Dust', icon: Orbit, desc: 'Slow-drifting stars + nebula with halos' },
  { id: 'video', label: 'Video', icon: Film, desc: 'Custom video background loop' },
  { id: 'off', label: 'Off', icon: Ban, desc: 'No animated background' },
];

const DEFAULT_SETTINGS = {
  speed: 50,
  density: 50,
  opacity: 80,
  primary_color: '#FF7814',
  secondary_color: '#DAA520',
  scope: 'fullpage',
};

export default function ThemeTab({ siteSettings, onSettingsUpdate }) {
  const [theme, setTheme] = useState(siteSettings?.motion_theme || 'embers');
  const [videoUrl, setVideoUrl] = useState(siteSettings?.motion_video_url || '');
  const [settings, setSettings] = useState({
    ...DEFAULT_SETTINGS,
    ...(siteSettings?.theme_settings || {}),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [playerTheme, setPlayerTheme] = useState(siteSettings?.player_theme || 'classic');
  const [playerAccent, setPlayerAccent] = useState(siteSettings?.player_accent || '');
  const [ekgSpeed, setEkgSpeed] = useState(siteSettings?.ekg_speed || 50);
  const [ekgColor, setEkgColor] = useState(siteSettings?.ekg_color || '#FF3B30');
  const [ekgEnabled, setEkgEnabled] = useState(siteSettings?.ekg_enabled !== false);
  const [worldColor, setWorldColor] = useState(siteSettings?.world_color || '');
  const [emberEffect, setEmberEffect] = useState(siteSettings?.ember_effect || 'embers');
  const [emberColor1, setEmberColor1] = useState(siteSettings?.ember_color_1 || '#FF7814');
  const [emberColor2, setEmberColor2] = useState(siteSettings?.ember_color_2 || '#DAA520');
  const [emberColor3, setEmberColor3] = useState(siteSettings?.ember_color_3 || '#B41414');

  // EKG advanced (stored inside customize dict)
  const c0 = siteSettings?.customize || {};
  const [ekgGradient, setEkgGradient] = useState(c0.ekg_gradient || ['#FF3B30', '#DAA520', '#00F0FF']);
  const [ekgLineWidth, setEkgLineWidth] = useState(c0.ekg_line_width || 2);
  const [ekgRaysEnabled, setEkgRaysEnabled] = useState(c0.ekg_rays_enabled !== false);
  const [ekgRaysColor, setEkgRaysColor] = useState(c0.ekg_rays_color || '#FF3B30');
  const [ekgRaysIntensity, setEkgRaysIntensity] = useState(c0.ekg_rays_intensity ?? 70);

  // Equalizer bar settings
  const [eqColors, setEqColors] = useState(c0.equalizer_colors || ['#FF3B30', '#DAA520', '#8B0000']);
  const [eqBarCount, setEqBarCount] = useState(c0.equalizer_bar_count || 48);
  const [eqBarGap, setEqBarGap] = useState(c0.equalizer_bar_gap ?? 3);
  const [eqSpeed, setEqSpeed] = useState(c0.equalizer_speed || 50);
  const [eqGlow, setEqGlow] = useState(c0.equalizer_glow ?? 60);
  const [eqHeight, setEqHeight] = useState(c0.equalizer_height || 280);
  const [eqPeakColor, setEqPeakColor] = useState(c0.equalizer_peak_color || '#FFFFFF');

  const videoInputRef = useRef(null);

  useEffect(() => {
    if (siteSettings) {
      setTheme(siteSettings.motion_theme || 'embers');
      setVideoUrl(siteSettings.motion_video_url || '');
      setSettings(prev => ({ ...DEFAULT_SETTINGS, ...prev, ...(siteSettings.theme_settings || {}) }));
      setPlayerTheme(siteSettings.player_theme || 'classic');
      setPlayerAccent(siteSettings.player_accent || '');
      setEkgSpeed(siteSettings.ekg_speed || 50);
      setEkgColor(siteSettings.ekg_color || '#FF3B30');
      setEkgEnabled(siteSettings.ekg_enabled !== false);
      setWorldColor(siteSettings.world_color || '');
      setEmberEffect(siteSettings.ember_effect || 'embers');
      setEmberColor1(siteSettings.ember_color_1 || '#FF7814');
      setEmberColor2(siteSettings.ember_color_2 || '#DAA520');
      setEmberColor3(siteSettings.ember_color_3 || '#B41414');
      const c = siteSettings.customize || {};
      setEkgGradient(c.ekg_gradient || ['#FF3B30', '#DAA520', '#00F0FF']);
      setEkgLineWidth(c.ekg_line_width || 2);
      setEkgRaysEnabled(c.ekg_rays_enabled !== false);
      setEkgRaysColor(c.ekg_rays_color || '#FF3B30');
      setEkgRaysIntensity(c.ekg_rays_intensity ?? 70);
      setEqColors(c.equalizer_colors || ['#FF3B30', '#DAA520', '#8B0000']);
      setEqBarCount(c.equalizer_bar_count || 48);
      setEqBarGap(c.equalizer_bar_gap ?? 3);
      setEqSpeed(c.equalizer_speed || 50);
      setEqGlow(c.equalizer_glow ?? 60);
      setEqHeight(c.equalizer_height || 280);
      setEqPeakColor(c.equalizer_peak_color || '#FFFFFF');
    }
  }, [siteSettings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) return toast.error('Video must be under 100MB');

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`${API}/upload/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5min for large uploads
      });
      if (data.url) {
        setVideoUrl(data.url);
        toast.success('Video uploaded!');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? `Upload failed: ${detail}` : 'Failed to upload video');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveTheme = async () => {
    setSaving(true);
    try {
      const payload = {
        motion_theme: theme,
        motion_video_url: videoUrl,
        theme_settings: settings,
        player_theme: playerTheme,
        player_accent: playerAccent,
        ekg_speed: ekgSpeed,
        ekg_color: ekgColor,
        ekg_enabled: ekgEnabled,
        world_color: worldColor,
        ember_effect: emberEffect,
        ember_color_1: emberColor1,
        ember_color_2: emberColor2,
        ember_color_3: emberColor3,
        customize: {
          ...(siteSettings?.customize || {}),
          ekg_gradient: ekgGradient,
          ekg_line_width: ekgLineWidth,
          ekg_rays_enabled: ekgRaysEnabled,
          ekg_rays_color: ekgRaysColor,
          ekg_rays_intensity: ekgRaysIntensity,
          equalizer_colors: eqColors,
          equalizer_bar_count: eqBarCount,
          equalizer_bar_gap: eqBarGap,
          equalizer_speed: eqSpeed,
          equalizer_glow: eqGlow,
          equalizer_height: eqHeight,
          equalizer_peak_color: eqPeakColor,
        },
      };
      const { data } = await api.put(`${API}/site-settings`, payload);
      if (onSettingsUpdate) onSettingsUpdate(data);
      toast.success('Theme saved!');
    } catch (err) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const currentEffect = EFFECT_OPTIONS.find(e => e.id === theme);

  return (
    <div className="space-y-6 pb-32" data-testid="theme-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">
            BACKGROUND THEME
          </h2>
          <p className="text-sm text-[#A3A3A3] mt-1">Customize the animated background effect on your homepage</p>
        </div>
        <Button onClick={saveTheme} disabled={saving}
          className="bg-[#DAA520] hover:bg-[#C4941C] text-black font-bold px-6" data-testid="save-theme-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          SAVE THEME
        </Button>
      </div>

      {/* ── EKG HEARTBEAT LINE ── */}
      <div className="space-y-4 p-4 bg-[#141414] border border-[#262626] rounded-xl">
        <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> EKG Heartbeat Line
        </Label>
        <p className="text-xs text-[#737373]">The pulsating EKG line above the hero. Gradient colors flow across, pulsating rays emit from the cursor dot.</p>

        {/* Gradient color stops */}
        <div>
          <Label className="text-xs text-[#A3A3A3]">Line Gradient ({ekgGradient.length} stops)</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {ekgGradient.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="color" value={c}
                  onChange={(e) => {
                    const next = [...ekgGradient];
                    next[i] = e.target.value;
                    setEkgGradient(next);
                  }}
                  className="w-9 h-9 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                  data-testid={`ekg-grad-${i}`} />
                <input type="text" value={c}
                  onChange={(e) => {
                    const next = [...ekgGradient];
                    next[i] = e.target.value;
                    setEkgGradient(next);
                  }}
                  className="flex-1 bg-[#0A0A0A] border border-[#262626] text-[#E5E5E5] font-mono text-[10px] outline-none px-1.5 py-1 rounded w-0" />
                {ekgGradient.length > 2 && (
                  <button onClick={() => setEkgGradient(ekgGradient.filter((_, idx) => idx !== i))}
                    className="text-[#737373] hover:text-red-400 text-xs px-1" data-testid={`ekg-grad-del-${i}`}>×</button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            {ekgGradient.length < 5 && (
              <button onClick={() => setEkgGradient([...ekgGradient, '#FFFFFF'])}
                className="text-[10px] px-2 py-1 bg-[#0A0A0A] border border-[#262626] text-[#A3A3A3] hover:border-[#DAA520] rounded" data-testid="ekg-grad-add">
                + Add Color Stop
              </button>
            )}
            <div className="flex-1 h-4 rounded border border-[#262626]"
              style={{ background: `linear-gradient(90deg, ${ekgGradient.join(',')})` }} />
          </div>
        </div>

        {/* Line width */}
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-xs text-[#A3A3A3]">Line Thickness</Label>
            <span className="text-xs text-[#DAA520] font-mono">{ekgLineWidth}px</span>
          </div>
          <input type="range" min="1" max="8" value={ekgLineWidth}
            onChange={(e) => setEkgLineWidth(parseInt(e.target.value))}
            className="w-full mt-1 accent-[#DAA520] h-2" data-testid="ekg-line-width" />
        </div>

        {/* Speed */}
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-xs text-[#A3A3A3]">Sweep Speed</Label>
            <span className="text-xs text-[#DAA520] font-mono">{ekgSpeed}%</span>
          </div>
          <input type="range" min="10" max="100" value={ekgSpeed}
            onChange={(e) => setEkgSpeed(parseInt(e.target.value))}
            className="w-full mt-1 accent-[#DAA520] h-2" data-testid="ekg-speed" />
        </div>

        {/* Pulsating rays */}
        <div className="pt-3 border-t border-[#262626] space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-[#A3A3A3]">Pulsating Rays</Label>
            <button onClick={() => setEkgRaysEnabled(!ekgRaysEnabled)}
              className={`w-10 h-5 rounded-full relative transition-all ${ekgRaysEnabled ? 'bg-[#DAA520]' : 'bg-[#333]'}`}
              data-testid="ekg-rays-toggle">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all ${ekgRaysEnabled ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>
          {ekgRaysEnabled && (
            <>
              <div>
                <Label className="text-xs text-[#A3A3A3]">Ray Color</Label>
                <div className="flex gap-2 mt-1 items-center">
                  <input type="color" value={ekgRaysColor}
                    onChange={(e) => setEkgRaysColor(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                    data-testid="ekg-ray-color" />
                  <Input value={ekgRaysColor}
                    onChange={(e) => setEkgRaysColor(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] font-mono text-xs flex-1" />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-[#A3A3A3]">Ray Intensity</Label>
                  <span className="text-xs text-[#DAA520] font-mono">{ekgRaysIntensity}%</span>
                </div>
                <input type="range" min="0" max="100" value={ekgRaysIntensity}
                  onChange={(e) => setEkgRaysIntensity(parseInt(e.target.value))}
                  className="w-full mt-1 accent-[#DAA520] h-2" data-testid="ekg-rays-intensity" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── BOTTOM EQUALIZER BARS ── */}
      <div className="space-y-4 p-4 bg-[#141414] border border-[#262626] rounded-xl">
        <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
          <Radio className="w-3.5 h-3.5" /> Bottom Equalizer Bars
        </Label>
        <p className="text-xs text-[#737373]">The sound-reactive EQ bars at the bottom (replaces the old wave). Fully customizable.</p>

        {/* Gradient stops */}
        <div>
          <Label className="text-xs text-[#A3A3A3]">Bar Gradient (top → bottom, {eqColors.length} stops)</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {eqColors.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="color" value={c}
                  onChange={(e) => {
                    const next = [...eqColors];
                    next[i] = e.target.value;
                    setEqColors(next);
                  }}
                  className="w-9 h-9 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                  data-testid={`eq-color-${i}`} />
                <input type="text" value={c}
                  onChange={(e) => {
                    const next = [...eqColors];
                    next[i] = e.target.value;
                    setEqColors(next);
                  }}
                  className="flex-1 bg-[#0A0A0A] border border-[#262626] text-[#E5E5E5] font-mono text-[10px] outline-none px-1.5 py-1 rounded w-0" />
                {eqColors.length > 2 && (
                  <button onClick={() => setEqColors(eqColors.filter((_, idx) => idx !== i))}
                    className="text-[#737373] hover:text-red-400 text-xs px-1" data-testid={`eq-color-del-${i}`}>×</button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            {eqColors.length < 5 && (
              <button onClick={() => setEqColors([...eqColors, '#FFFFFF'])}
                className="text-[10px] px-2 py-1 bg-[#0A0A0A] border border-[#262626] text-[#A3A3A3] hover:border-[#DAA520] rounded" data-testid="eq-color-add">
                + Add Stop
              </button>
            )}
            <div className="flex-1 h-4 rounded border border-[#262626]"
              style={{ background: `linear-gradient(180deg, ${eqColors.join(',')})` }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Bar Count</Label>
              <span className="text-xs text-[#DAA520] font-mono">{eqBarCount}</span>
            </div>
            <input type="range" min="12" max="120" value={eqBarCount}
              onChange={(e) => setEqBarCount(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="eq-bar-count" />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Bar Gap</Label>
              <span className="text-xs text-[#DAA520] font-mono">{eqBarGap}px</span>
            </div>
            <input type="range" min="0" max="12" value={eqBarGap}
              onChange={(e) => setEqBarGap(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="eq-bar-gap" />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Speed</Label>
              <span className="text-xs text-[#DAA520] font-mono">{eqSpeed}%</span>
            </div>
            <input type="range" min="10" max="100" value={eqSpeed}
              onChange={(e) => setEqSpeed(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="eq-speed" />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Glow</Label>
              <span className="text-xs text-[#DAA520] font-mono">{eqGlow}%</span>
            </div>
            <input type="range" min="0" max="100" value={eqGlow}
              onChange={(e) => setEqGlow(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="eq-glow" />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Height</Label>
              <span className="text-xs text-[#DAA520] font-mono">{eqHeight}px</span>
            </div>
            <input type="range" min="120" max="480" step="20" value={eqHeight}
              onChange={(e) => setEqHeight(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="eq-height" />
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3]">Peak Cap Color</Label>
            <div className="flex gap-2 mt-1 items-center">
              <input type="color" value={eqPeakColor}
                onChange={(e) => setEqPeakColor(e.target.value)}
                className="w-9 h-9 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                data-testid="eq-peak-color" />
              <Input value={eqPeakColor}
                onChange={(e) => setEqPeakColor(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] font-mono text-[10px] flex-1 h-9" />
            </div>
          </div>
        </div>
      </div>

      {/* Effect Selector */}
      <div className="space-y-3">
        <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
          <Palette className="w-3.5 h-3.5" /> Background Effect
        </Label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="effect-grid">
          {EFFECT_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const active = theme === opt.id;
            return (
              <button key={opt.id} onClick={() => setTheme(opt.id)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                  active
                    ? 'border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520]'
                    : 'border-[#262626] bg-[#141414] text-[#737373] hover:border-[#404040] hover:text-[#A3A3A3]'
                }`}
                data-testid={`effect-${opt.id}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-bold uppercase">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {currentEffect && (
          <p className="text-xs text-[#737373] italic">{currentEffect.desc}</p>
        )}
      </div>

      {/* Video Upload - only show when video theme selected */}
      {theme === 'video' && (
        <div className="space-y-3 p-4 bg-[#141414] border border-[#262626] rounded-xl">
          <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Film className="w-3.5 h-3.5" /> Video Background
          </Label>
          <p className="text-xs text-[#737373]">Upload a video loop (MP4 recommended, max 100MB). It will play muted on loop behind your content.</p>
          <div className="flex gap-3 items-center">
            <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
            <Button onClick={() => videoInputRef.current?.click()} disabled={uploading}
              variant="outline" className="border-[#404040] text-[#A3A3A3]" data-testid="upload-video-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {videoUrl ? 'Change Video' : 'Upload Video'}
            </Button>
            {videoUrl && <span className="text-xs text-[#22C55E]">Video uploaded</span>}
          </div>
          {videoUrl && (
            <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Or paste video URL..."
              className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] text-xs"
              data-testid="video-url-input" />
          )}
        </div>
      )}

      {/* Scope Selector */}
      {theme !== 'off' && (
        <div className="space-y-3">
          <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Display Scope
          </Label>
          <p className="text-xs text-[#737373]">Where should the background effect appear?</p>
          <div className="grid grid-cols-2 gap-2" data-testid="scope-selector">
            <button onClick={() => updateSetting('scope', 'hero')}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                settings.scope === 'hero'
                  ? 'border-[#DAA520] bg-[#DAA520]/10'
                  : 'border-[#262626] bg-[#141414] hover:border-[#404040]'
              }`} data-testid="scope-hero">
              <div className="text-sm font-bold uppercase text-[#E5E5E5]">Hero Section Only</div>
              <div className="text-[10px] text-[#737373] mt-1">Effect appears behind the header area only</div>
            </button>
            <button onClick={() => updateSetting('scope', 'fullpage')}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                settings.scope === 'fullpage'
                  ? 'border-[#DAA520] bg-[#DAA520]/10'
                  : 'border-[#262626] bg-[#141414] hover:border-[#404040]'
              }`} data-testid="scope-fullpage">
              <div className="text-sm font-bold uppercase text-[#E5E5E5]">Full Page</div>
              <div className="text-[10px] text-[#737373] mt-1">Effect covers the entire page background</div>
            </button>
          </div>
        </div>
      )}

      {/* Effect Controls */}
      {theme !== 'off' && theme !== 'video' && (
        <div className="space-y-4 p-4 bg-[#141414] border border-[#262626] rounded-xl">
          <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Wind className="w-3.5 h-3.5" /> Effect Controls
          </Label>

          {/* Speed */}
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Speed</Label>
              <span className="text-xs text-[#DAA520] font-mono">{settings.speed}%</span>
            </div>
            <input type="range" min="10" max="100" value={settings.speed}
              onChange={(e) => updateSetting('speed', parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="speed-slider" />
            <div className="flex justify-between text-[9px] text-[#525252]"><span>Slow</span><span>Fast</span></div>
          </div>

          {/* Density */}
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Density</Label>
              <span className="text-xs text-[#DAA520] font-mono">{settings.density}%</span>
            </div>
            <input type="range" min="10" max="100" value={settings.density}
              onChange={(e) => updateSetting('density', parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="density-slider" />
            <div className="flex justify-between text-[9px] text-[#525252]"><span>Sparse</span><span>Dense</span></div>
          </div>

          {/* Opacity */}
          <div>
            <div className="flex justify-between items-center">
              <Label className="text-xs text-[#A3A3A3]">Opacity</Label>
              <span className="text-xs text-[#DAA520] font-mono">{settings.opacity}%</span>
            </div>
            <input type="range" min="10" max="100" value={settings.opacity}
              onChange={(e) => updateSetting('opacity', parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="opacity-slider" />
            <div className="flex justify-between text-[9px] text-[#525252]"><span>Subtle</span><span>Vivid</span></div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#A3A3A3]">Primary Color</Label>
              <div className="flex gap-2 mt-1.5 items-center">
                <input type="color" value={settings.primary_color}
                  onChange={(e) => updateSetting('primary_color', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                  data-testid="primary-color" />
                <Input value={settings.primary_color}
                  onChange={(e) => updateSetting('primary_color', e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] font-mono text-xs flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3]">Secondary Color</Label>
              <div className="flex gap-2 mt-1.5 items-center">
                <input type="color" value={settings.secondary_color}
                  onChange={(e) => updateSetting('secondary_color', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-[#404040] cursor-pointer bg-transparent"
                  data-testid="secondary-color" />
                <Input value={settings.secondary_color}
                  onChange={(e) => updateSetting('secondary_color', e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] font-mono text-xs flex-1" />
              </div>
            </div>
          </div>

          {/* Color Presets */}
          <div>
            <Label className="text-xs text-[#737373]">Quick Presets</Label>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {[
                { label: 'Fire', p: '#FF7814', s: '#DAA520' },
                { label: 'Ice', p: '#96DCFF', s: '#64B4DC' },
                { label: 'Neon', p: '#00FF88', s: '#8B5CF6' },
                { label: 'Blood', p: '#FF3B30', s: '#8B0000' },
                { label: 'Royal', p: '#DAA520', s: '#4B0082' },
                { label: 'Ocean', p: '#0EA5E9', s: '#0369A1' },
                { label: 'Toxic', p: '#AAFF00', s: '#006400' },
                { label: 'Sunset', p: '#FF6B6B', s: '#FFA500' },
              ].map(preset => (
                <button key={preset.label} onClick={() => {
                  updateSetting('primary_color', preset.p);
                  updateSetting('secondary_color', preset.s);
                }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] hover:border-[#404040] transition-colors"
                  data-testid={`preset-${preset.label.toLowerCase()}`}>
                  <div className="w-3 h-3 rounded-full" style={{ background: `linear-gradient(135deg, ${preset.p}, ${preset.s})` }} />
                  <span className="text-[10px] font-bold uppercase text-[#A3A3A3]">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Live Preview */}
      {theme !== 'off' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-[#DAA520] font-bold uppercase tracking-wider flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Live Preview
            </Label>
            <button onClick={() => setShowPreview(!showPreview)}
              className="text-[10px] text-[#737373] uppercase font-bold hover:text-[#DAA520] transition-colors">
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
          {showPreview && (
            <div className="relative w-full h-[200px] rounded-xl overflow-hidden border-2 border-[#262626] bg-[#050505]" data-testid="theme-preview">
              <MotionTheme
                theme={theme}
                videoUrl={videoUrl}
                settings={settings}
              />
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <span className="text-xl font-black uppercase tracking-wider opacity-30 text-white">PREVIEW</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
